import type {
  PdfAnnotation,
  ProjectFile,
  ProjectFileStream,
  TradeBriefingPdfAnnotation,
  TradeBriefingRelease,
} from '../types';
import { mergeStreamVersions } from './projectFileStreamVersions';

export type TradeBriefingFileGroupKind = 'two_d_pdf' | 'three_d_html' | 'general';

export type TradeBriefingFileVersionGroup = {
  key: string;
  kind: TradeBriefingFileGroupKind;
  title: string;
  versions: Array<{ fileId: string; label: string }>;
};

export function isTradeBriefingPdfFile(file: Pick<ProjectFile, 'mimeType' | 'name'>): boolean {
  const mime = (file.mimeType || '').toLowerCase();
  if (mime === 'application/pdf') return true;
  return /\.pdf$/i.test(file.name || '');
}

function isTradeBriefingHtmlFile(file: Pick<ProjectFile, 'mimeType' | 'name'>): boolean {
  const mime = (file.mimeType || '').toLowerCase();
  if (mime === 'text/html' || mime === 'application/xhtml+xml') return true;
  return /\.html?$/i.test(file.name || '');
}

export function generalTradeBriefingFileGroupKey(fileId: string): string {
  return `general:${fileId}`;
}

function versionOptionLabel(file: ProjectFile): string {
  const label = file.versionLabel?.trim();
  if (label) return label;
  if (typeof file.versionIndex === 'number') return `v${file.versionIndex}`;
  return file.name || 'File';
}


/**
 * 3D HTML tracks plus unversioned general-document PDFs that staff can attach to
 * a trade briefing. Versioned 2D drawing tracks stay out — those are client-portal
 * engineered drawings, not addenda. Quote-section PDFs in Other project files
 * are included.
 */
export function buildTradeBriefingFileGroups(
  streams: ProjectFileStream[],
  files: ProjectFile[],
): TradeBriefingFileVersionGroup[] {
  const groups: TradeBriefingFileVersionGroup[] = [];
  const filesByStream = new Map<string, ProjectFile[]>();

  for (const file of files) {
    if (!file.id || file.fileSection === 'quote') continue;
    if (!file.streamId) continue;
    const list = filesByStream.get(file.streamId) || [];
    list.push(file);
    filesByStream.set(file.streamId, list);
  }

  const sortedStreams = [...streams].sort((a, b) =>
    a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }),
  );
  for (const stream of sortedStreams) {
    if (stream.streamRole === 'supplementary') continue;
    if (stream.streamKind !== 'three_d_html') continue;
    const streamId = stream.id;
    if (!streamId) continue;
    // Structure forks inherit the pre-split history of their root.
    const own = filesByStream.get(streamId) || [];
    const inherited = stream.forkedFromStreamId ? filesByStream.get(stream.forkedFromStreamId) || [] : [];
    const versions = mergeStreamVersions(own, inherited).filter(
      file => file.id && isTradeBriefingHtmlFile(file),
    );
    if (!versions.length) continue;
    const headId = versions[0]?.id;
    groups.push({
      key: streamId,
      kind: 'three_d_html',
      title: stream.title || '3D Model',
      versions: versions.map(file => ({
        fileId: file.id!,
        label: `${versionOptionLabel(file)}${file.id === headId ? ' (current)' : ''}`,
      })),
    });
  }

  const generalPdfs = files
    .filter(
      file =>
        Boolean(file.id) &&
        !file.streamId &&
        isTradeBriefingPdfFile(file),
    )
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));

  for (const file of generalPdfs) {
    groups.push({
      key: generalTradeBriefingFileGroupKey(file.id!),
      kind: 'general',
      title: file.name || 'PDF',
      versions: [{ fileId: file.id!, label: 'Include' }],
    });
  }

  return groups;
}

export function selectionsFromTradeBriefingFileIds(
  groups: TradeBriefingFileVersionGroup[],
  ids: string[],
): Record<string, string> {
  const wanted = new Set(ids.filter(Boolean));
  const next: Record<string, string> = {};
  for (const group of groups) {
    const match = group.versions.find(version => wanted.has(version.fileId));
    next[group.key] = match?.fileId ?? '';
  }
  return next;
}

export function isTradeBriefingHtmlRelease(
  release: Pick<TradeBriefingRelease, 'streamKind' | 'mimeType' | 'name'>,
): boolean {
  return (
    release.streamKind === 'three_d_html' ||
    (release.mimeType || '').toLowerCase() === 'text/html' ||
    (release.mimeType || '').toLowerCase() === 'application/xhtml+xml' ||
    /\.html?$/i.test(release.name || '')
  );
}

/** Public briefing label: addendum PDFs use the filename; models keep their track name. */
export function tradeBriefingPublicFileLabel(
  release: Pick<TradeBriefingRelease, 'streamKind' | 'mimeType' | 'name' | 'displayLabel'>,
): string {
  if (isTradeBriefingHtmlRelease(release)) {
    return release.displayLabel || release.name || '3D Model';
  }
  return release.name || release.displayLabel || 'PDF';
}

/** PDFs are unversioned addenda. 3D models show the hub version. */
export function tradeBriefingPublicVersionLabel(
  release: Pick<TradeBriefingRelease, 'streamKind' | 'mimeType' | 'name' | 'versionLabel' | 'releaseVersionIndex'>,
): string {
  if (!isTradeBriefingHtmlRelease(release)) return 'Unversioned';
  const named = release.versionLabel?.trim();
  if (named) return named;
  if (typeof release.releaseVersionIndex === 'number' && release.releaseVersionIndex > 0) {
    return `v${release.releaseVersionIndex}`;
  }
  return 'Versioned';
}

function parseBriefingIsoDate(iso?: string): Date {
  if (!iso) return new Date(0);
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? new Date(0) : d;
}

export function briefingAnnotationsToPdf(
  rows: TradeBriefingPdfAnnotation[] | undefined,
  documentId: string,
  documentUrl: string,
): PdfAnnotation[] {
  return (rows ?? []).map(row => ({
    id: row.id,
    documentId,
    documentUrl,
    leadId: '',
    pageNumber: row.pageNumber,
    x: row.x,
    y: row.y,
    content: row.content,
    userId: '',
    userName: row.userName,
    createdAt: parseBriefingIsoDate(row.createdAt),
    replies: (row.replies ?? []).map(reply => ({
      userId: '',
      userName: reply.userName,
      content: reply.content,
      createdAt: parseBriefingIsoDate(reply.createdAt),
    })),
  }));
}
